/**
 * Reads a Blob and resolves with its base64-encoded contents (without the
 * `data:<mime>;base64,` prefix).
 * @param blob - The Blob to encode.
 * @returns The base64-encoded contents of the blob.
 */
const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () =>
      reject(reader.error ?? new Error('Failed to read blob'));
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Unexpected FileReader result'));

        return;
      }

      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.readAsDataURL(blob);
  });

/**
 * Triggers a download of a given Blob object as a file with the specified filename.
 * - Creates a temporary anchor element to initiate the download.
 * - Cleans up the element and revokes the object URL after download.
 * @param data - The Blob data to download.
 * @param filename - The name for the downloaded file.
 */
const downloadBlobViaAnchor = (data: Blob, filename: string) => {
  const blob: Blob = new Blob([data], {
    type: data.type,
  });
  const a: HTMLAnchorElement = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(a.href);
};

/**
 * Hands a Blob to the Android native download bridge when present (converting
 * it to base64 first, since the WebView can't open `blob:` URLs), otherwise
 * runs the given fallback, or the default browser anchor-click download.
 * @param blob - The Blob to save.
 * @param fileName - The name for the saved file.
 * @param mimeType - The MIME type of the file.
 * @param fallback - Browser-only behaviour to run when the bridge isn't present.
 * @returns A promise that resolves once the blob has been handed off.
 */
export const saveBlob = async (
  blob: Blob,
  fileName: string,
  mimeType: string,
  fallback?: () => void | Promise<void>,
) => {
  if (window.AndroidDownload?.save) {
    const base64 = await blobToBase64(blob);
    window.AndroidDownload.save(base64, fileName, mimeType);

    return;
  }

  if (fallback) {
    await fallback();

    return;
  }

  downloadBlobViaAnchor(blob, fileName);
};

/**
 * Triggers a download of a given Blob object as a file with the specified filename.
 * Routes through the Android native bridge when present, falling back to the
 * browser anchor-click download otherwise.
 * @param data - The Blob data to download.
 * @param filename - The name for the downloaded file.
 * @returns A promise that resolves once the file has been handed off.
 */
export const downloadFile = (data: Blob, filename: string) =>
  saveBlob(data, filename, data.type);
